import type { DictionaryQuery, PageQuery } from '@/schemas/courses'

/**
 * Query key factory for Courses.
 *
 * One branch per response shape, the rule `features/brokers/api/keys.ts`
 * states. The catalog (`list`) and the home shelves both carry
 * course cards, but they are different lists — one key each.
 *
 * `progress()` is the prefix of every read that carries the MEMBER'S progress
 * (a course's lecture ticks, the resume shelf, my-learning, certificates, the
 * path's done steps) or their own records (purchases, payment attempts), so a
 * write that changes them can mark all of them stale in one call without
 * touching the catalog.
 */
export const courseKeys = {
  all: ['courses'] as const,

  // ── Catalog: the same for every member ────────────────────────────────────

  list: () => [...courseKeys.all, 'list'] as const,
  reviews: (courseId: number, query: PageQuery) =>
    [...courseKeys.all, 'reviews', courseId, query] as const,
  questions: (query: PageQuery) => [...courseKeys.all, 'questions', query] as const,
  /** `q` arrives trimmed and `null` when empty — see `dictionaryQuerySchema`. */
  dictionary: (query: DictionaryQuery) => [...courseKeys.all, 'dictionary', query] as const,
  dictionaryTerm: (slug: string) => [...courseKeys.all, 'dictionary-term', slug] as const,
  verify: (code: string) => [...courseKeys.all, 'verify', code] as const,

  // ── Member progress ────────────────────────────────────────────────────────

  progress: () => [...courseKeys.all, 'progress'] as const,
  detail: (courseId: number) => [...courseKeys.progress(), 'detail', courseId] as const,
  home: () => [...courseKeys.progress(), 'home'] as const,
  myLearning: () => [...courseKeys.progress(), 'my-learning'] as const,
  purchased: () => [...courseKeys.progress(), 'purchased'] as const,
  payments: () => [...courseKeys.progress(), 'payments'] as const,
  certificates: () => [...courseKeys.progress(), 'certificates'] as const,
  path: () => [...courseKeys.progress(), 'path'] as const,
  pathStep: (stepId: number) => [...courseKeys.progress(), 'path-step', stepId] as const,
}
